import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SchoolProfile360 from '../components/SchoolProfile360';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import { StatCard, Modal } from '../components/shared';
import NBFCApprovalManager from '../components/admin/NBFCApprovalManager';
import OutreachTab from '../components/admin/OutreachTab';
import PricingConfigPanel from '../components/admin/PricingConfigPanel';
import AayushAIKeyPanel from '../components/admin/AayushAIKeyPanel';
import RevenueProjectionTile from '../components/admin/RevenueProjectionTile';
import VeoVideoPanel from '../components/admin/VeoVideoPanel';
import VideoAnalyticsTab from '../components/admin/VideoAnalyticsTab';
import SchoolDbCleanupPanel from '../components/admin/SchoolDbCleanupPanel';
import IndiaGeoMapPanel from '../components/admin/IndiaGeoMapPanel';
import OutreachWinsPanel from '../components/admin/OutreachWinsPanel';
import { ConversionProofPanel } from '../components/admin/ConversionProofPanel';
import LiveTestPanel from '../components/admin/LiveTestPanel';
import RichAnalyticsPanel from '../components/analytics/RichAnalyticsPanel';
import OutreachV2Panel from '../components/admin/OutreachV2Panel';
import SupportTicketsPanel from '../components/admin/SupportTicketsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const xhrJson = (method, endpoint, token, data = null) => new Promise((resolve) => {
  try {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API_URL}${endpoint}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (data) xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) return;
      try {
        resolve(xhr.responseText ? JSON.parse(xhr.responseText) : {});
      } catch (err) {
        console.error('XHR parse error:', err);
        resolve({});
      }
    };
    xhr.onerror = () => resolve({});
    xhr.send(data ? JSON.stringify(data) : null);
  } catch (err) {
    console.error('XHR error:', err);
    resolve({});
  }
});

// API helper
const api = {
  get: async (endpoint, token) => {
    try {
      return await xhrJson('GET', endpoint, token);
    } catch (err) {
      console.error('API error:', err);
      return {};
    }
  },
  post: async (endpoint, data, token) => {
    try {
      return await xhrJson('POST', endpoint, token, data);
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  },
  put: async (endpoint, data, token) => {
    try {
      return await xhrJson('PUT', endpoint, token, data);
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  }
};

// Add School Modal Component
import OnboardingWizard from '../components/OnboardingWizard';
import OnboardingAdminPanel from '../components/admin/OnboardingAdminPanel';
import UpcomingDemosWidget from '../components/admin/UpcomingDemosWidget';
import DikshaDemoEditor from '../components/admin/DikshaDemoEditor';
import BillingAdminPage from '../components/admin/BillingAdminPage';
import CommentsInboxPanel from '../components/admin/CommentsInboxPanel';
function AddSchoolModal({ isOpen, onClose, token, onSuccess }) {
  return <OnboardingWizard isOpen={isOpen} onClose={onClose} token={token} onSuccess={onSuccess} />;
}

// MRR & Onboarding Revenue Card
function OnboardingRevenueCard({ token, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await api.get('/api/onboarding/records', token);
        if (res?.success) setData(res);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [token]);

  if (loading) return <div className="bg-white rounded-xl p-6 text-center text-gray-400">Loading MRR data...</div>;
  if (!data || !data.records?.length) return (
    <div className="bg-white rounded-xl p-6 border border-dashed border-gray-300 text-center" data-testid="mrr-empty">
      <p className="text-gray-400 text-sm">{language === 'hi' ? 'अभी कोई onboarding record नहीं है। School onboard करें।' : 'No onboarding records yet. Onboard a school to see MRR.'}</p>
    </div>
  );

  const { records, total_revenue, monthly_recurring } = data;
  const activeSchools = records.filter(r => r.status === 'active').length;
  const totalSetupRevenue = records.reduce((sum, r) => sum + (r.setup_cost || 0), 0);
  const avgMRR = activeSchools > 0 ? Math.round(monthly_recurring / activeSchools) : 0;

  return (
    <div className="space-y-4" data-testid="mrr-revenue-section">
      {/* MRR Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <h3 className="text-lg font-semibold mb-4 opacity-90">
          {language === 'hi' ? '📈 MRR & Subscription Revenue' : '📈 MRR & Subscription Revenue'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold">₹{monthly_recurring.toLocaleString()}</p>
            <p className="text-sm opacity-80">{language === 'hi' ? 'Monthly MRR' : 'Monthly MRR'}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold">₹{(monthly_recurring * 12).toLocaleString()}</p>
            <p className="text-sm opacity-80">{language === 'hi' ? 'Yearly ARR' : 'Yearly ARR'}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold">₹{totalSetupRevenue.toLocaleString()}</p>
            <p className="text-sm opacity-80">{language === 'hi' ? 'Setup Revenue' : 'Setup Revenue'}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold">₹{total_revenue.toLocaleString()}</p>
            <p className="text-sm opacity-80">{language === 'hi' ? 'कुल Revenue' : 'Total Revenue'}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold">{activeSchools}</p>
            <p className="text-sm opacity-80">{language === 'hi' ? 'Active Schools' : 'Active Schools'}</p>
          </div>
        </div>
      </div>

      {/* School-wise Revenue List */}
      <div className="bg-white rounded-xl shadow p-4" data-testid="mrr-school-table">
        <h4 className="font-semibold text-gray-700 mb-3">{language === 'hi' ? 'School-wise Revenue' : 'School-wise Revenue'}</h4>
        <div className="space-y-3" data-testid="mrr-school-list">
          {records.map((r, idx) => (
            <div key={idx} className="rounded-2xl border border-slate-200 p-4 transition hover:border-indigo-200 hover:bg-slate-50">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{r.school_name}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs capitalize">{r.plan || 'standard'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.status || 'active'}
                    </span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">
                      {language === 'hi' ? 'Onboarded:' : 'Onboarded:'} {r.onboarded_at ? new Date(r.onboarded_at).toLocaleDateString('en-IN') : '-'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm min-w-[320px]">
                  <div className="rounded-xl bg-green-50 p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wide text-green-700">{language === 'hi' ? 'Monthly' : 'Monthly'}</p>
                    <p className="mt-1 font-bold text-green-700">₹{(r.monthly_fee || 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-xl bg-orange-50 p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wide text-orange-700">{language === 'hi' ? 'Setup' : 'Setup'}</p>
                    <p className="mt-1 font-bold text-orange-700">₹{(r.setup_cost || 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-xl bg-indigo-50 p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wide text-indigo-700">{language === 'hi' ? '1st Payment' : '1st Payment'}</p>
                    <p className="mt-1 font-bold text-indigo-700">₹{(r.first_payment || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {avgMRR > 0 && (
          <div className="mt-3 pt-3 border-t flex justify-between items-center text-sm">
            <span className="text-gray-500">{language === 'hi' ? 'Average MRR per school' : 'Avg MRR/school'}</span>
            <span className="font-bold text-indigo-600">₹{avgMRR.toLocaleString()}/mo</span>
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformDashboard({ token: propToken }) {
  const { language } = useLanguage();
  const location = useLocation();
  // Use prop token or fallback to localStorage (most reliable after redirect)
  const token = propToken || localStorage.getItem('token');
  
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [walletOverview, setWalletOverview] = useState(null);
  const [schoolWallets, setSchoolWallets] = useState([]);
  const [platformTransactions, setPlatformTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState(location.pathname === '/schools' ? 'schools' : 'overview');

  // Sidebar nav routes /dashboard and /schools both render this component — keep
  // the internal tab in sync with the URL so "Schools" in the sidebar actually opens it.
  useEffect(() => {
    if (location.pathname === '/schools') setActiveTab('schools');
    else if (location.pathname === '/dashboard') setActiveTab('overview');
  }, [location.pathname]);
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [lookupsData, setLookupsData] = useState(null);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  
  // New states for enhanced features
  const [aiAnalytics, setAiAnalytics] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [resettingId, setResettingId] = useState(null);
  const [show360, setShow360] = useState(false);
  const [plansOverview, setPlansOverview] = useState(null);
  const [complaintsStats, setComplaintsStats] = useState(null);
  const [allComplaints, setAllComplaints] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolDetail, setSchoolDetail] = useState(null);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [addCreditsSchoolId, setAddCreditsSchoolId] = useState(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [addCreditsReason, setAddCreditsReason] = useState('');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  
  // New states for additional features
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dataTasksSchools, setDataTasksSchools] = useState([]);  // Schools with pending data tasks
  const [conversionProofData, setConversionProofData] = useState(null);

  useEffect(() => { 
    if (token) {
      loadAllData(token);
    } else {
      // Retry once after short delay (token may arrive late)
      const retryTimer = setTimeout(() => {
        const t = localStorage.getItem('token');
        if (t) {
          loadAllData(t);
        } else {
          setLoading(false);
        }
      }, 500);
      return () => clearTimeout(retryTimer);
    }

    // Failsafe: never let the page stay in "Loading..." for more than 4s.
    // Even if every API call hangs, the chrome should render so the user can navigate.
    const failsafe = setTimeout(() => setLoading(false), 4000);
    return () => clearTimeout(failsafe);
  }, [token]);

  const loadConversionProofPanel = async () => {
    try {
      const t = token || localStorage.getItem('token');
      const proofRes = await api.get('/api/conversion-proof/panel', t);
      setConversionProofData(proofRes?.success ? proofRes : null);
    } catch (err) {
      console.error('Reload conversion proof failed:', err);
    }
  };

  const loadAllData = async (authToken) => {
    const t = authToken || token;
    if (!t) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Critical-path: schools + overview unlock the main UI. Render as soon as they arrive.
    const critical = Promise.allSettled([
      api.get('/api/schools/', t),
      api.get('/api/wallet/platform/overview', t),
    ]);

    critical.then(([schoolsRes, overviewRes]) => {
      if (schoolsRes.status === 'fulfilled') setSchools(schoolsRes.value?.schools || []);
      if (overviewRes.status === 'fulfilled') setWalletOverview(overviewRes.value?.overview);
      setLoading(false);
    });

    // Non-critical secondary data — fire-and-forget, never blocks the page.
    api.get('/api/wallet/platform/transactions?limit=10', t)
      .then((res) => setPlatformTransactions(res?.transactions || []))
      .catch((err) => console.error('Transactions load failed:', err));

    api.get('/api/data-completion/schools-list', t)
      .then((res) => setDataTasksSchools(res?.schools || []))
      .catch((err) => console.error('Data tasks load failed:', err));

    api.get('/api/conversion-proof/panel', t)
      .then((res) => setConversionProofData(res?.success ? res : null))
      .catch((err) => console.error('Conversion proof load failed:', err));

    api.get('/api/wallet/platform/schools?sort_by=balance&order=asc', t)
      .then((walletsRes) => setSchoolWallets(walletsRes?.schools || []))
      .catch((err) => console.error('Wallet schools load failed:', err));
  };

  const loadAIAnalytics = async () => {
    try {
      const res = await api.get('/api/platform-admin/ai-analytics?period=30days', token);
      setAiAnalytics(res);
    } catch (err) {
      console.error('Error loading AI analytics:', err);
    }
  };

  const loadUserStats = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/api/platform-admin/users/stats', token),
        api.get('/api/platform-admin/users?limit=20', token)
      ]);
      setUserStats(statsRes?.stats);
      setAllUsers(usersRes?.users || []);
    } catch (err) {
      console.error('Error loading user stats:', err);
    }
  };

  const loadPlansOverview = async () => {
    try {
      const res = await api.get('/api/platform-admin/plans/overview', token);
      setPlansOverview(res);
    } catch (err) {
      console.error('Error loading plans:', err);
    }
  };

  const loadComplaints = async () => {
    try {
      const [statsRes, complaintsRes] = await Promise.all([
        api.get('/api/platform-admin/complaints/stats', token),
        api.get('/api/platform-admin/complaints?limit=20', token)
      ]);
      setComplaintsStats(statsRes?.stats);
      setAllComplaints(complaintsRes?.complaints || []);
    } catch (err) {
      console.error('Error loading complaints:', err);
    }
  };

  const resetUserPassword = async (user) => {
    if (!window.confirm(`${user.name} (${user.phone}) का password reset करें? एक अस्थायी password बनेगा और उन्हें WhatsApp पर भेजा जाएगा।`)) return;
    setResettingId(user.id);
    try {
      const res = await api.post(`/api/platform-admin/users/${user.id}/reset-password`, {}, token);
      if (res?.success) {
        const waLine = res.sent_whatsapp ? '\n\n✅ यह password WhatsApp पर भी भेज दिया गया है।' : '\n\n⚠️ WhatsApp नहीं भेजा जा सका — कृपया यह password खुद user को दें।';
        window.alert(`✅ Password reset हो गया!\n\n👤 ${user.name}\n📱 Login: ${res.phone || user.phone}\n🔑 अस्थायी Password: ${res.temp_password}${waLine}\n\n(User अगली बार login करते ही नया password सेट करेगा।)`);
      } else {
        window.alert('❌ Reset नहीं हो पाया। दोबारा कोशिश करें।');
      }
    } catch (e) {
      window.alert('❌ ' + (e?.response?.data?.detail || 'Reset में दिक्कत आई'));
    } finally {
      setResettingId(null);
    }
  };

  const loadSchoolDetail = async (schoolId) => {
    try {
      const res = await api.get(`/api/platform-admin/school/${schoolId}/detail`, token);
      setSchoolDetail(res);
      setSelectedSchool(schoolId);
    } catch (err) {
      console.error('Error loading school detail:', err);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'ai-analytics' && !aiAnalytics) loadAIAnalytics();
    if (tab === 'users' && !userStats) loadUserStats();
    if (tab === 'plans' && !plansOverview) loadPlansOverview();
    if (tab === 'support' && !complaintsStats) loadComplaints();
    if (tab === 'audit' && auditLogs.length === 0) loadAuditLogs();
    if (tab === 'health') loadHealthDashboard();
    if (tab === 'lookups') loadLookupsAnalytics();
  };

  const loadHealthDashboard = async () => {
    setHealthLoading(true);
    try {
      const res = await api.get('/api/platform-admin/health-dashboard', token);
      setHealthData(res);
    } catch (err) {
      console.error('Health dashboard load failed:', err);
    } finally {
      setHealthLoading(false);
    }
  };

  const loadLookupsAnalytics = async () => {
    setLookupsLoading(true);
    try {
      const res = await api.get('/api/platform-admin/my-school-lookups-analytics?days=30&limit=100', token);
      setLookupsData(res);
    } catch (err) {
      console.error('Lookups analytics load failed:', err);
    } finally {
      setLookupsLoading(false);
    }
  };

  const handleApprove = async (schoolId) => {
    await api.post(`/api/schools/${schoolId}/approve`, {}, token);
    loadAllData();
  };

  const handleAddCredits = async () => {
    try {
      await api.post(`/api/platform-admin/school/${addCreditsSchoolId}/add-credits`, {
        amount: parseFloat(addCreditsAmount),
        reason: addCreditsReason
      }, token);
      setShowAddCreditsModal(false);
      setAddCreditsAmount('');
      setAddCreditsReason('');
      loadAllData();
      if (selectedSchool) loadSchoolDetail(selectedSchool);
    } catch (err) {
      alert('Error adding credits');
    }
  };

  const handleSendNotification = async () => {
    try {
      await api.post('/api/platform-admin/notifications/send', {
        title: notificationTitle,
        message: notificationMessage,
        target: 'all'
      }, token);
      setShowNotificationModal(false);
      setNotificationTitle('');
      setNotificationMessage('');
      alert(language === 'hi' ? 'Notification भेज दिया गया!' : 'Notification sent!');
    } catch (err) {
      alert('Error sending notification');
    }
  };

  const handleUpdateComplaintStatus = async (complaintId, status) => {
    try {
      await api.post(`/api/platform-admin/complaints/${complaintId}/update`, { status }, token);
      loadComplaints();
    } catch (err) {
      alert('Error updating complaint');
    }
  };

  const loadAuditLogs = async () => {
    try {
      const [logsRes, statsRes, feedRes] = await Promise.all([
        api.get('/api/audit/logs?limit=30', token),
        api.get('/api/audit/logs/stats?days=7', token),
        api.get('/api/utils/activity-feed?limit=15', token)
      ]);
      setAuditLogs(logsRes?.logs || []);
      setAuditStats(statsRes?.stats);
      setActivityFeed(feedRes?.activities || []);
    } catch (err) {
      console.error('Error loading audit logs:', err);
    }
  };

  const handleSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await api.get(`/api/utils/search?q=${encodeURIComponent(query)}`, token);
      setSearchResults(res?.results || []);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const handleExport = async (dataType) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/utils/export/${dataType}?format=csv`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="platform-dashboard">
      <div className="flex justify-between items-center">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
          {language === 'hi' ? '🏛️ प्लेटफ़ॉर्म Dashboard' : '🏛️ Platform Dashboard'}
        </h2>
        <div className="flex gap-1.5 sm:gap-2 flex-wrap">
          <button 
            onClick={() => setShowExportModal(true)} 
            className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-xs sm:text-sm"
          >
            📥 Export
          </button>
          <button 
            onClick={() => setShowNotificationModal(true)} 
            className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs sm:text-sm"
          >
            📢 <span className="hidden sm:inline">{language === 'hi' ? 'Notification भेजें' : 'Send Notification'}</span><span className="sm:hidden">Notify</span>
          </button>
          <button onClick={() => setShowAddModal(true)} className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-xs sm:text-sm">
            + <span className="hidden sm:inline">Add School</span><span className="sm:hidden">School</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation - scrollable on mobile */}
      <div className="flex gap-2 border-b pb-2 overflow-x-auto -mx-2 px-2">
        {[
          { id: 'overview', icon: '📊', label: 'Overview' },
          { id: 'analytics', icon: '📈', label: 'Analytics' },
          { id: 'outreach-v2', icon: '🎯', label: 'Outreach v2' },
          { id: 'health', icon: '🏥', label: 'Health' },
          { id: 'lookups', icon: '🔍', label: 'Lookups' },
          { id: 'outreach', icon: '📣', label: 'Launch Outreach' },
          { id: 'ai-analytics', icon: '🤖', label: 'AI Analytics' },
          { id: 'wallets', icon: '💳', label: 'Wallet Tracking' },
          { id: 'users', icon: '👥', label: 'Users' },
          { id: 'plans', icon: '📋', label: 'Plans' },
          { id: 'pricing', icon: '💰', label: 'Pricing Config' },
          { id: 'aayush-key', icon: '🔑', label: 'Aayush AI Key' },
          { id: 'veo', icon: '🎬', label: 'AI Videos' },
          { id: 'video-analytics', icon: '📊', label: 'Video Analytics' },
          { id: 'school-db-cleanup', icon: '🧹', label: 'School DB Cleanup' },
          { id: 'geo-map', icon: '🗺', label: 'India Map' },
          { id: 'outreach-wins', icon: '🏆', label: 'Outreach Wins' },
          { id: 'support', icon: '🎫', label: 'Support' },
          { id: 'audit', icon: '📝', label: 'Audit Logs' },
          { id: 'transactions', icon: '💰', label: 'Transactions' },
          { id: 'schools', icon: '🏫', label: 'Schools' },
          { id: 'nbfc-approval', icon: '🏦', label: 'NBFC Approval' },
          { id: 'onboarding-cal', icon: '📅', label: 'Onboarding Calls' },
          { id: 'diksha-editor', icon: '🎬', label: 'Diksha Demo Editor' },
          { id: 'billing', icon: '💳', label: 'Billing' },
          { id: 'comments-inbox', icon: '💬', label: 'Comments Inbox' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            data-testid={`platform-tab-${tab.id}`}
            className={`px-2.5 sm:px-3 py-1.5 sm:py-2 font-medium rounded-lg text-xs sm:text-sm whitespace-nowrap shrink-0 ${
              activeTab === tab.id 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.icon} {language === 'hi' ? tab.label : tab.label}
          </button>
        ))}
      </div>

      {/* Global Search Bar */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); handleSearch(e.target.value); }}
          placeholder={language === 'hi' ? '🔍 Search schools, users, students...' : '🔍 Search schools, users, students...'}
          className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 bg-white"
          data-testid="global-search"
        />
        {searchResults.length > 0 && searchQuery.length >= 2 && (
          <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg mt-1 z-50 max-h-80 overflow-y-auto">
            {searchResults.map((result, idx) => (
              <div key={idx} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center gap-3">
                <span className="text-2xl">{result.icon}</span>
                <div>
                  <p className="font-medium">{result.title}</p>
                  <p className="text-sm text-gray-500">{result.subtitle}</p>
                </div>
                <span className="ml-auto px-2 py-1 bg-gray-100 rounded text-xs">{result.type}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lookups Tab - My School Lookups Analytics */}
      {activeTab === 'lookups' && (
        <div className="space-y-6" data-testid="lookups-dashboard">
          {lookupsLoading && !lookupsData ? (
            <div className="text-center py-16 text-gray-500">
              <div className="inline-block w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3">Loading analytics...</p>
            </div>
          ) : lookupsData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-xl p-5 shadow-lg" data-testid="lookups-total">
                  <p className="text-white/80 text-xs font-semibold uppercase">Total (30d)</p>
                  <p className="text-3xl font-black mt-1">{lookupsData.summary.total_lookups_recent}</p>
                  <p className="text-white/70 text-xs mt-1">All-time: {lookupsData.summary.total_lookups_all_time}</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl p-5 shadow-lg">
                  <p className="text-white/80 text-xs font-semibold uppercase">Found</p>
                  <p className="text-3xl font-black mt-1">{lookupsData.summary.found}</p>
                  <p className="text-white/70 text-xs mt-1">In master DB</p>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-amber-600 text-white rounded-xl p-5 shadow-lg">
                  <p className="text-white/80 text-xs font-semibold uppercase">Not Found</p>
                  <p className="text-3xl font-black mt-1">{lookupsData.summary.not_found}</p>
                  <p className="text-white/70 text-xs mt-1">Manual entry</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl p-5 shadow-lg">
                  <p className="text-white/80 text-xs font-semibold uppercase">Unique Codes</p>
                  <p className="text-3xl font-black mt-1">{lookupsData.summary.unique_codes_searched}</p>
                  <p className="text-white/70 text-xs mt-1">Distinct schools</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-xl p-5 shadow-lg">
                  <p className="text-white/80 text-xs font-semibold uppercase">Find Rate</p>
                  <p className="text-3xl font-black mt-1">{lookupsData.summary.find_rate_percent}%</p>
                  <p className="text-white/70 text-xs mt-1">Match quality</p>
                </div>
              </div>

              {/* Hot Leads Section */}
              {lookupsData.hot_leads && lookupsData.hot_leads.length > 0 && (
                <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="hot-leads-section">
                  <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-100">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                      🔥 Hot Leads <span className="text-xs font-normal text-slate-500">(Most-searched schools — contact them!)</span>
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">School</th>
                          <th className="px-4 py-3 text-left font-semibold">Location</th>
                          <th className="px-4 py-3 text-center font-semibold">UDISE</th>
                          <th className="px-4 py-3 text-center font-semibold">Board</th>
                          <th className="px-4 py-3 text-center font-semibold">Students</th>
                          <th className="px-4 py-3 text-center font-semibold">Searches</th>
                          <th className="px-4 py-3 text-center font-semibold">Last</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {lookupsData.hot_leads.map((lead, idx) => (
                          <tr key={idx} className="hover:bg-orange-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{lead.school_name || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 text-xs">{lead.district || ''}, {lead.state || ''}</td>
                            <td className="px-4 py-3 text-center font-mono text-xs">{lead.udise_code}</td>
                            <td className="px-4 py-3 text-center text-xs">{lead.board || '—'}</td>
                            <td className="px-4 py-3 text-center">{lead.total_students || '—'}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">{lead.lookup_count}x</span>
                            </td>
                            <td className="px-4 py-3 text-center text-xs text-slate-500">
                              {lead.last_lookup ? new Date(lead.last_lookup).toLocaleDateString('hi-IN') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top States & Districts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* States */}
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b font-semibold text-slate-800">🗺️ Top States</div>
                  <div className="divide-y">
                    {lookupsData.top_states.length === 0 ? (
                      <p className="p-4 text-sm text-slate-400 text-center">No data yet</p>
                    ) : lookupsData.top_states.map((s, i) => (
                      <div key={i} className="p-3 flex items-center justify-between hover:bg-slate-50">
                        <span className="font-medium text-slate-800">{s.state}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">{s.unique_schools_count} schools</span>
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">{s.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Districts */}
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b font-semibold text-slate-800">📍 Top Districts</div>
                  <div className="divide-y">
                    {lookupsData.top_districts.length === 0 ? (
                      <p className="p-4 text-sm text-slate-400 text-center">No data yet</p>
                    ) : lookupsData.top_districts.map((d, i) => (
                      <div key={i} className="p-3 flex items-center justify-between hover:bg-slate-50">
                        <span className="font-medium text-slate-800 text-sm">
                          {d.district} <span className="text-xs text-slate-400">· {d.state}</span>
                        </span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Lookups Table */}
              <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="recent-lookups-table">
                <div className="p-4 bg-slate-50 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">🕐 Recent Lookups ({lookupsData.recent_lookups.length})</h3>
                  <button onClick={loadLookupsAnalytics} className="text-xs px-3 py-1 bg-slate-200 hover:bg-slate-300 rounded-full font-semibold">🔄 Refresh</button>
                </div>
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Time</th>
                        <th className="px-3 py-2 text-left font-semibold">UDISE</th>
                        <th className="px-3 py-2 text-left font-semibold">School / District</th>
                        <th className="px-3 py-2 text-center font-semibold">Found</th>
                        <th className="px-3 py-2 text-center font-semibold">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {lookupsData.recent_lookups.length === 0 ? (
                        <tr><td colSpan="5" className="p-4 text-center text-slate-400">No recent lookups</td></tr>
                      ) : lookupsData.recent_lookups.map((r, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-xs text-slate-500">{r.timestamp ? new Date(r.timestamp).toLocaleString('hi-IN') : '—'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.udise_code}</td>
                          <td className="px-3 py-2 text-xs">
                            {r.found ? (
                              <>
                                <div className="font-medium text-slate-800">{r.school_name || '—'}</div>
                                <div className="text-slate-500">{r.district}, {r.state}</div>
                              </>
                            ) : <span className="text-red-500 italic">Not in database</span>}
                          </td>
                          <td className="px-3 py-2 text-center">{r.found ? '✅' : '❌'}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${r.source === 'public' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {r.source}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marketing Insight Tip */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
                <h4 className="font-semibold text-indigo-900 mb-2">💡 How to use this data</h4>
                <ul className="text-sm text-indigo-800 space-y-1">
                  <li>• <strong>Hot Leads</strong> → schools searched 2+ times are strong buy-signals. Reach out via Campaign Manager with a personalized offer.</li>
                  <li>• <strong>Top States/Districts</strong> → focus FB/Google Ads budget where organic demand is highest.</li>
                  <li>• <strong>Not Found</strong> → principals whose schools are missing. Offer "manual onboarding" CTA via follow-up.</li>
                  <li>• <strong>Low Find Rate</strong> → expand UDISE master DB coverage for that region.</li>
                </ul>
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <button onClick={loadLookupsAnalytics} className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold">
                🔍 Load Lookups Analytics
              </button>
            </div>
          )}
        </div>
      )}

      {/* Launch Outreach Tab */}
      {activeTab === 'outreach' && (
        <OutreachTab token={token} language={language} data-testid="outreach-tab" />
      )}

      {/* Rich Analytics Tab */}
      {activeTab === 'analytics' && (
        <RichAnalyticsPanel token={token} mode="platform" />
      )}

      {/* Outreach v2 Tab — Channel Router + Email Bulk */}
      {activeTab === 'outreach-v2' && (
        <OutreachV2Panel token={token} />
      )}

      {/* Health Tab - Launch Health Dashboard */}
      {activeTab === 'health' && (
        <div className="space-y-6" data-testid="health-dashboard">
          {healthLoading && !healthData ? (
            <div className="text-center py-16 text-gray-500">
              <div className="inline-block w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3">Loading health status...</p>
            </div>
          ) : healthData ? (
            <>
              {/* Health Score Card */}
              <div className={`rounded-2xl p-6 shadow-xl text-white ${
                healthData.health_score >= 85 ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                healthData.health_score >= 70 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                'bg-gradient-to-r from-red-500 to-rose-600'
              }`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-white/80 text-sm font-semibold uppercase tracking-wider">
                      🏥 {language === 'hi' ? 'Launch Health Status' : 'Launch Health Status'}
                    </p>
                    <h2 className="text-5xl font-black mt-1">{healthData.health_score}<span className="text-2xl text-white/70">/100</span></h2>
                    <p className="text-white/90 text-sm mt-1">
                      {language === 'hi' ? 'Last checked:' : 'Last checked:'} {new Date(healthData.timestamp).toLocaleString('hi-IN')}
                    </p>
                  </div>
                  <div className="flex gap-3 text-sm flex-wrap">
                    <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-3 text-center">
                      <p className="text-3xl font-bold">{healthData.summary.ok}</p>
                      <p className="text-xs">✅ OK</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-3 text-center">
                      <p className="text-3xl font-bold">{healthData.summary.warn}</p>
                      <p className="text-xs">⚠️ WARN</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-3 text-center">
                      <p className="text-3xl font-bold">{healthData.summary.fail}</p>
                      <p className="text-xs">❌ FAIL</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-3 text-center">
                      <p className="text-3xl font-bold">{healthData.summary.info}</p>
                      <p className="text-xs">ℹ️ INFO</p>
                    </div>
                    <button
                      onClick={loadHealthDashboard}
                      className="bg-white text-gray-800 rounded-xl px-5 py-3 font-semibold hover:bg-white/90"
                      data-testid="health-refresh-btn"
                    >
                      🔄 Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Checks grouped by Category */}
              {Object.entries(healthData.checks.reduce((acc, c) => {
                (acc[c.category] = acc[c.category] || []).push(c);
                return acc;
              }, {})).map(([category, checks]) => (
                <div key={category} className="bg-white rounded-xl shadow">
                  <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b font-semibold text-slate-800 rounded-t-xl">
                    📁 {category}
                  </div>
                  <div className="divide-y">
                    {checks.map((check, idx) => {
                      const conf = {
                        ok:   { bg: 'bg-green-50',  border: 'border-l-green-500',  icon: '✅', text: 'text-green-700' },
                        warn: { bg: 'bg-yellow-50', border: 'border-l-yellow-500', icon: '⚠️', text: 'text-yellow-700' },
                        fail: { bg: 'bg-red-50',    border: 'border-l-red-500',    icon: '❌', text: 'text-red-700' },
                        info: { bg: 'bg-blue-50',   border: 'border-l-blue-500',   icon: 'ℹ️', text: 'text-blue-700' },
                      }[check.status] || { bg: '', border: '', icon: '•', text: '' };
                      return (
                        <div key={idx} className={`p-4 ${conf.bg} border-l-4 ${conf.border} flex flex-wrap items-start gap-4`} data-testid={`health-check-${check.name.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                          <span className="text-2xl shrink-0">{conf.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-semibold ${conf.text}`}>{check.name}</p>
                            <p className="text-sm text-slate-600 mt-1">{check.detail}</p>
                            {check.action && (
                              <p className="text-xs text-slate-500 mt-1.5 italic">💡 Action: {check.action}</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${conf.text} bg-white shadow-sm`}>
                            {check.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Helpful tips */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
                <h4 className="font-semibold text-indigo-900 mb-2">💡 Tips</h4>
                <ul className="text-sm text-indigo-800 space-y-1">
                  <li>• Health Score 85+ → Ready for launch ✅</li>
                  <li>• WARN states block specific features only, not entire app</li>
                  <li>• FAIL states must be fixed before live school onboarding</li>
                  <li>• Refresh after fixing any issue to see updated status</li>
                </ul>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">
              <button onClick={loadHealthDashboard} className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold">
                🏥 Load Health Dashboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <UpcomingDemosWidget token={token} />
          <LiveTestPanel language={language} />
          <ConversionProofPanel data={conversionProofData} language={language} onRefresh={loadConversionProofPanel} />
          {/* Schools Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard title={language === 'hi' ? 'कुल स्कूल' : 'Total Schools'} value={schools.length} icon="🏫" color="orange" />
            <StatCard title={language === 'hi' ? 'Approved' : 'Approved'} value={schools.filter(s => s.status === 'approved').length} icon="✅" color="green" />
            <StatCard title={language === 'hi' ? 'Pending' : 'Pending'} value={schools.filter(s => s.status === 'pending').length} icon="⏳" color="blue" />
            <StatCard title={language === 'hi' ? 'कुल Students' : 'Total Students'} value={schools.reduce((acc, s) => acc + (s.student_count || 0), 0)} icon="👨‍🎓" color="purple" />
          </div>

          {/* Revenue Stats */}
          {walletOverview && (
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-4 opacity-90">
                {language === 'hi' ? '💰 Platform Revenue' : '💰 Platform Revenue'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold">₹{walletOverview.total_revenue}</p>
                  <p className="text-sm opacity-80">{language === 'hi' ? 'कुल Revenue' : 'Total Revenue'}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">₹{walletOverview.this_month_revenue}</p>
                  <p className="text-sm opacity-80">{language === 'hi' ? 'इस महीने' : 'This Month'}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">₹{walletOverview.total_ai_spent}</p>
                  <p className="text-sm opacity-80">{language === 'hi' ? 'AI खर्च' : 'AI Spent'}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">₹{walletOverview.platform_profit}</p>
                  <p className="text-sm opacity-80">{language === 'hi' ? 'Platform Profit' : 'Platform Profit'}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">{walletOverview.total_ai_calls}</p>
                  <p className="text-sm opacity-80">{language === 'hi' ? 'AI Calls' : 'AI Calls'}</p>
                </div>
              </div>
            </div>
          )}

          {/* MRR & Onboarding Revenue */}
          <OnboardingRevenueCard token={token} language={language} />

          {/* Wallet Health Stats */}
          {walletOverview && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow p-5 text-center">
                <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल Wallet Balance' : 'Total Wallet Balance'}</p>
                <p className="text-2xl font-bold text-green-600 mt-1">₹{walletOverview.total_wallet_balance}</p>
              </div>
              <div className="bg-white rounded-xl shadow p-5 text-center">
                <p className="text-gray-500 text-sm">{language === 'hi' ? 'Wallets Active' : 'Active Wallets'}</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{walletOverview.total_schools_with_wallet}</p>
              </div>
              <div className="bg-white rounded-xl shadow p-5 text-center border-2 border-yellow-400">
                <p className="text-gray-500 text-sm">{language === 'hi' ? '⚠️ Low Balance' : '⚠️ Low Balance'}</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{walletOverview.low_balance_schools}</p>
              </div>
              <div className="bg-white rounded-xl shadow p-5 text-center border-2 border-red-400">
                <p className="text-gray-500 text-sm">{language === 'hi' ? '🚨 Zero Balance' : '🚨 Zero Balance'}</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{walletOverview.zero_balance_schools}</p>
              </div>
            </div>
          )}

          {/* Revenue & Wallet Charts */}
          {walletOverview && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Distribution Pie Chart */}
              <div className="bg-white rounded-xl shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-4">📊 {language === 'hi' ? 'Revenue Distribution' : 'Revenue Distribution'}</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: language === 'hi' ? 'AI खर्च' : 'AI Cost', value: parseFloat(walletOverview.total_ai_spent) || 0 },
                        { name: language === 'hi' ? 'Platform Profit' : 'Platform Profit', value: parseFloat(walletOverview.platform_profit) || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#f97316" />
                      <Cell fill="#22c55e" />
                    </Pie>
                    <Tooltip formatter={(value) => `₹${value}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Wallet Health Pie Chart */}
              <div className="bg-white rounded-xl shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-4">💳 {language === 'hi' ? 'Wallet Health' : 'Wallet Health'}</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: language === 'hi' ? 'Healthy' : 'Healthy', value: (walletOverview.total_schools_with_wallet || 0) - (walletOverview.low_balance_schools || 0) - (walletOverview.zero_balance_schools || 0) },
                        { name: language === 'hi' ? 'Low Balance' : 'Low Balance', value: walletOverview.low_balance_schools || 0 },
                        { name: language === 'hi' ? 'Zero Balance' : 'Zero Balance', value: walletOverview.zero_balance_schools || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#22c55e" />
                      <Cell fill="#eab308" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Schools Distribution Bar Chart */}
          {schools.length > 0 && (
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-4">🏫 {language === 'hi' ? 'Schools Status Distribution' : 'Schools Status Distribution'}</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[
                  { name: language === 'hi' ? 'Approved' : 'Approved', value: schools.filter(s => s.status === 'approved').length, fill: '#22c55e' },
                  { name: language === 'hi' ? 'Pending' : 'Pending', value: schools.filter(s => s.status === 'pending').length, fill: '#eab308' },
                  { name: language === 'hi' ? 'Rejected' : 'Rejected', value: schools.filter(s => s.status === 'rejected').length, fill: '#ef4444' }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {[
                      { name: 'Approved', fill: '#22c55e' },
                      { name: 'Pending', fill: '#eab308' },
                      { name: 'Rejected', fill: '#ef4444' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* School Health Score Section */}
          <SchoolHealthSection token={token} language={language} />

          {/* Data Tasks by School - NEW */}
          {dataTasksSchools.length > 0 && (
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-4">📋 {language === 'hi' ? 'Schools with Pending Data Tasks' : 'Schools with Pending Data Tasks'}</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dataTasksSchools.filter(s => s.pending_tasks > 0).slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [`${value} tasks`, 'Pending']} />
                    <Bar dataKey="pending_tasks" fill="#f97316" radius={[0, 8, 8, 0]} name={language === 'hi' ? 'Pending Tasks' : 'Pending Tasks'} />
                  </BarChart>
                </ResponsiveContainer>

                {/* Summary List */}
                <div className="space-y-3" data-testid="data-tasks-school-list">
                  {dataTasksSchools.slice(0, 5).map((school) => (
                    <div key={school.id} className={`rounded-2xl border p-4 ${school.pending_tasks > 0 ? 'border-orange-200 bg-orange-50/70' : 'border-green-200 bg-green-50/70'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900 truncate" title={school.name}>{school.name}</p>
                          <p className="mt-1 text-xs text-slate-500">{language === 'hi' ? 'Total Tasks' : 'Total Tasks'}: {school.total_tasks}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          school.pending_tasks > 100 ? 'bg-red-100 text-red-700' :
                          school.pending_tasks > 50 ? 'bg-yellow-100 text-yellow-700' :
                          school.pending_tasks > 0 ? 'bg-orange-100 text-orange-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {language === 'hi' ? 'Pending' : 'Pending'}: {school.pending_tasks}
                        </span>
                      </div>
                    </div>
                  ))}
                  {dataTasksSchools.reduce((sum, s) => sum + s.pending_tasks, 0) > 0 && (
                    <div className="mt-3 p-3 bg-orange-100 rounded-lg text-center">
                      <p className="text-sm text-orange-800 font-medium">
                        {language === 'hi' ? '⚠️ कुल' : '⚠️ Total'} {dataTasksSchools.reduce((sum, s) => sum + s.pending_tasks, 0)} {language === 'hi' ? 'pending tasks सभी स्कूलों में' : 'pending tasks across all schools'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Wallet Tracking Tab */}
      {activeTab === 'wallets' && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800">
              {language === 'hi' ? '💳 सभी स्कूलों का Wallet Status' : '💳 All Schools Wallet Status'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="school-wallets-table">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'स्कूल' : 'School'}</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'Status' : 'Status'}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">{language === 'hi' ? 'Balance' : 'Balance'}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">{language === 'hi' ? 'कुल Recharged' : 'Total Recharged'}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">{language === 'hi' ? 'इस महीने खर्च' : 'This Month Spent'}</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'AI Calls' : 'AI Calls'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {schoolWallets.map((sw) => (
                  <tr key={sw.school_id} className={`hover:bg-gray-50 ${sw.status === 'critical' ? 'bg-red-50' : sw.status === 'warning' ? 'bg-yellow-50' : ''}`}>
                    <td className="px-4 py-3 font-medium">{sw.school_name}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sw.status === 'critical' ? 'bg-red-100 text-red-700' :
                        sw.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {sw.status === 'critical' ? '🚨 Critical' : sw.status === 'warning' ? '⚠️ Low' : '✅ Healthy'}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${
                      sw.balance <= 0 ? 'text-red-600' : sw.balance < 100 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      ₹{sw.balance}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">₹{sw.total_recharged}</td>
                    <td className="px-4 py-3 text-right text-orange-600">₹{sw.this_month_spent}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{sw.this_month_calls}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800">
              {language === 'hi' ? '📋 हाल के Platform Transactions' : '📋 Recent Platform Transactions'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="platform-transactions-table">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'तारीख' : 'Date'}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'स्कूल' : 'School'}</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'प्रकार' : 'Type'}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'विवरण' : 'Description'}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">{language === 'hi' ? 'राशि' : 'Amount'}</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'Status' : 'Status'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {platformTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      {language === 'hi' ? 'कोई transactions नहीं' : 'No transactions yet'}
                    </td>
                  </tr>
                ) : (
                  platformTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        {new Date(tx.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 font-medium text-sm">{tx.school_name}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          tx.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {tx.type === 'credit' ? '➕ Credit' : '➖ Debit'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {tx.category === 'recharge' ? 'Wallet Recharge' : tx.category === 'ai_usage' ? `AI: ${tx.feature_name || ''}` : tx.notes || tx.category}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold ${
                        tx.type === 'credit' ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {tx.type === 'credit' ? '+' : '-'}₹{tx.amount?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          tx.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {tx.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Schools Tab */}
      {activeTab === 'schools' && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4">{language === 'hi' ? '🏫 Schools List' : '🏫 Schools List'}</h3>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">School Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Board</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Students</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {schools.map(school => (
                <tr key={school.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => loadSchoolDetail(school.id)}>
                  <td className="px-4 py-3 font-medium text-blue-600 hover:underline">{school.name}</td>
                  <td className="px-4 py-3">{school.board}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${school.status === 'approved' ? 'bg-green-100 text-green-700' : school.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                      {school.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{school.student_count}</td>
                  <td className="px-4 py-3 space-x-2">
                    {school.status === 'pending' && (
                      <button onClick={(e) => {e.stopPropagation(); handleApprove(school.id);}} className="text-green-600 hover:text-green-800">Approve</button>
                    )}
                    <button 
                      onClick={(e) => {e.stopPropagation(); setAddCreditsSchoolId(school.id); setShowAddCreditsModal(true);}}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + Credits
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AI Analytics Tab */}
      {activeTab === 'ai-analytics' && (
        <div className="space-y-6">
          {aiAnalytics ? (
            <>
              {/* AI Total Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल AI Calls' : 'Total AI Calls'}</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{aiAnalytics.total?.calls || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल AI लागत' : 'Total AI Cost'}</p>
                  <p className="text-3xl font-bold text-red-500 mt-1">₹{aiAnalytics.total?.cost || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">Input Tokens</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{(aiAnalytics.total?.input_tokens / 1000).toFixed(1)}K</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">Output Tokens</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{(aiAnalytics.total?.output_tokens / 1000).toFixed(1)}K</p>
                </div>
              </div>

              {/* Top Schools */}
              <div className="bg-white rounded-xl shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-4">🏆 {language === 'hi' ? 'Top Schools (AI Usage)' : 'Top Schools (AI Usage)'}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm">#</th>
                        <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'स्कूल' : 'School'}</th>
                        <th className="px-4 py-2 text-right text-sm">Calls</th>
                        <th className="px-4 py-2 text-right text-sm">{language === 'hi' ? 'लागत' : 'Cost'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {aiAnalytics.top_schools?.map((s, idx) => (
                        <tr key={s.school_id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{idx + 1}</td>
                          <td className="px-4 py-2">{s.school_name}</td>
                          <td className="px-4 py-2 text-right">{s.calls}</td>
                          <td className="px-4 py-2 text-right font-semibold text-green-600">₹{s.cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Features */}
              <div className="bg-white rounded-xl shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-4">🔧 {language === 'hi' ? 'Popular AI Features' : 'Popular AI Features'}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {aiAnalytics.top_features?.slice(0, 10).map((f, idx) => (
                    <div key={f.feature} className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 truncate">{f.feature?.replace(/_/g, ' ')}</p>
                      <p className="text-lg font-bold text-blue-600">{f.calls}</p>
                      <p className="text-xs text-green-600">₹{f.cost}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tier Distribution */}
              <div className="bg-white rounded-xl shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-4">📊 {language === 'hi' ? 'Tier Distribution' : 'Tier Distribution'}</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Tier Stats Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(aiAnalytics.tier_distribution || {}).map(([tier, data]) => (
                      <div key={tier} className={`rounded-lg p-4 text-center ${
                        tier === '1' ? 'bg-green-50 border-2 border-green-300' :
                        tier === '2' ? 'bg-blue-50 border-2 border-blue-300' :
                        'bg-purple-50 border-2 border-purple-300'
                      }`}>
                        <p className="font-medium">Tier {tier}</p>
                        <p className="text-2xl font-bold">{data.calls}</p>
                        <p className="text-sm text-gray-600">₹{data.cost}</p>
                      </div>
                    ))}
                  </div>
                  {/* Tier Pie Chart */}
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={Object.entries(aiAnalytics.tier_distribution || {}).map(([tier, data]) => ({
                          name: `Tier ${tier}`,
                          value: data.calls
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        <Cell fill="#22c55e" />
                        <Cell fill="#3b82f6" />
                        <Cell fill="#a855f7" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Daily Trend Chart */}
              {aiAnalytics.daily_trend && aiAnalytics.daily_trend.length > 0 && (
                <div className="bg-white rounded-xl shadow p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">📈 {language === 'hi' ? 'Daily AI Usage Trend' : 'Daily AI Usage Trend'}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={aiAnalytics.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(date) => date?.slice(5) || ''} />
                      <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                      <YAxis yAxisId="right" orientation="right" stroke="#22c55e" />
                      <Tooltip 
                        formatter={(value, name) => [name === 'cost' ? `₹${value}` : value, name === 'cost' ? 'Cost' : 'Calls']}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="calls" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name={language === 'hi' ? 'AI Calls' : 'AI Calls'} />
                      <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} name={language === 'hi' ? 'Cost (₹)' : 'Cost (₹)'} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top Features Bar Chart */}
              {aiAnalytics.top_features && aiAnalytics.top_features.length > 0 && (
                <div className="bg-white rounded-xl shadow p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">📊 {language === 'hi' ? 'Top AI Features Usage' : 'Top AI Features Usage'}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={aiAnalytics.top_features.slice(0, 8).map(f => ({
                      feature: f.feature?.replace(/_/g, ' ').slice(0, 15) || 'Unknown',
                      calls: f.calls,
                      cost: f.cost
                    }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="feature" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value, name) => [name === 'cost' ? `₹${value}` : value, name === 'cost' ? 'Cost' : 'Calls']} />
                      <Legend />
                      <Bar dataKey="calls" fill="#3b82f6" name={language === 'hi' ? 'Calls' : 'Calls'} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="cost" fill="#f97316" name={language === 'hi' ? 'Cost (₹)' : 'Cost (₹)'} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">Loading AI Analytics...</div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {userStats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल Users' : 'Total Users'}</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{userStats.total_users}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'Active (7 days)' : 'Active (7 days)'}</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{userStats.active_users_7days}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'Students' : 'Students'}</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{userStats.total_students}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'Teachers' : 'Teachers'}</p>
                  <p className="text-3xl font-bold text-orange-600 mt-1">{userStats.total_teachers}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'New This Month' : 'New This Month'}</p>
                  <p className="text-3xl font-bold text-red-500 mt-1">{userStats.new_users_this_month}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="p-4 bg-gray-50 border-b">
                  <h3 className="font-semibold">👥 {language === 'hi' ? 'Recent Users' : 'Recent Users'}</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'नाम' : 'Name'}</th>
                      <th className="px-4 py-2 text-left text-sm">Phone</th>
                      <th className="px-4 py-2 text-left text-sm">Role</th>
                      <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'स्कूल' : 'School'}</th>
                      <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'कार्रवाई' : 'Action'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allUsers.map(user => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{user.name}</td>
                        <td className="px-4 py-2">{user.phone}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.role === 'platform_admin' ? 'bg-red-100 text-red-700' :
                            user.role === 'school_admin' ? 'bg-blue-100 text-blue-700' :
                            user.role === 'teacher' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">{user.school_name || '-'}</td>
                        <td className="px-4 py-2">
                          {user.role !== 'platform_admin' && (
                            <button
                              onClick={() => resetUserPassword(user)}
                              disabled={resettingId === user.id}
                              data-testid={`reset-password-btn-${user.id}`}
                              className="px-3 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-medium hover:bg-amber-200 disabled:opacity-50 flex items-center gap-1">
                              {resettingId === user.id ? '⏳' : '🔑'} {language === 'hi' ? 'Password Reset' : 'Reset Password'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">Loading Users...</div>
          )}
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          {/* Revenue Projection Tile — real-time ARR estimate */}
          <RevenueProjectionTile token={token} language={language} />

          {plansOverview ? (
            <>
              {/* New Unified Pricing Model - No more Basic/Standard/Premium */}
              <div className="bg-gradient-to-r from-orange-50 via-amber-50 to-green-50 rounded-2xl p-6 border-2 border-orange-200" data-testid="unified-plan-banner">
                <h3 className="text-lg font-bold text-orange-700 mb-2">
                  🎓 {language === 'hi' ? 'Software Lifetime FREE + One-time ₹4999' : 'Software Lifetime FREE + One-time ₹4999'}
                </h3>
                <p className="text-sm text-slate-600">
                  {language === 'hi' 
                    ? 'सभी Schools एक ही Plan पर — Free ERP + AI (School की Gemini Key)। कोई Tier/Plan नहीं।' 
                    : 'All schools on one unified plan — Free ERP + AI (School\'s own Gemini Key). No tiers/plans.'}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow p-5 text-center border-2 border-orange-300">
                  <p className="text-gray-600 text-sm">{language === 'hi' ? 'कुल Active Schools' : 'Total Active Schools'}</p>
                  <p className="text-3xl font-bold text-orange-600">{plansOverview.schools?.length || 0}</p>
                </div>
                <div className="bg-green-50 rounded-xl shadow p-5 text-center border-2 border-green-300">
                  <p className="text-gray-600 text-sm">{language === 'hi' ? 'Total Setup Revenue (MRR)' : 'Total Setup Revenue (MRR)'}</p>
                  <p className="text-3xl font-bold text-green-600">₹{plansOverview.mrr}</p>
                </div>
                <div className="bg-blue-50 rounded-xl shadow p-5 text-center border-2 border-blue-300">
                  <p className="text-gray-600 text-sm">{language === 'hi' ? 'कुल Students' : 'Total Students'}</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {(plansOverview.schools || []).reduce((sum, s) => sum + (s.student_count || 0), 0)}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="p-4 bg-gray-50 border-b">
                  <h3 className="font-semibold">🏫 {language === 'hi' ? 'सभी Schools' : 'All Schools'}</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'स्कूल' : 'School'}</th>
                      <th className="px-4 py-2 text-center text-sm">Plan</th>
                      <th className="px-4 py-2 text-right text-sm">Setup Fee</th>
                      <th className="px-4 py-2 text-right text-sm">Wallet</th>
                      <th className="px-4 py-2 text-center text-sm">Students</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {plansOverview.schools?.map(s => (
                      <tr key={s.school_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{s.school_name}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            FREE + Setup
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">₹{s.monthly_fee}</td>
                        <td className="px-4 py-2 text-right font-semibold text-green-600">₹{s.wallet_balance}</td>
                        <td className="px-4 py-2 text-center">{s.student_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">Loading Plans...</div>
          )}
        </div>
      )}

      {/* Pricing Config Tab — Single Source of Truth admin panel */}
      {activeTab === 'pricing' && (
        <PricingConfigPanel token={token} language={language} />
      )}

      {/* Aayush AI Engine — Platform Gemini Key (BYOK) */}
      {activeTab === 'aayush-key' && (
        <AayushAIKeyPanel token={token} language={language} />
      )}

      {/* Veo AI Video Generator Tab */}
      {activeTab === 'veo' && (
        <VeoVideoPanel token={token} user={{ role: 'platform_admin' }} language={language} />
      )}

      {/* Video Analytics Tab */}
      {activeTab === 'video-analytics' && (
        <VideoAnalyticsTab token={token} language={language} />
      )}

      {/* School DB Cleanup Tab */}
      {activeTab === 'school-db-cleanup' && (
        <SchoolDbCleanupPanel token={token} language={language} />
      )}

      {/* India Geo Map Tab */}
      {activeTab === 'geo-map' && (
        <IndiaGeoMapPanel token={token} language={language} />
      )}

      {/* Outreach Wins Tab */}
      {activeTab === 'outreach-wins' && (
        <OutreachWinsPanel token={token} language={language} />
      )}

      {/* Support/Complaints Tab */}
      {activeTab === 'support' && (
        <div className="space-y-6">
          <SupportTicketsPanel token={token} language={language} />
          {complaintsStats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल Complaints' : 'Total Complaints'}</p>
                  <p className="text-3xl font-bold text-blue-600">{complaintsStats.total}</p>
                </div>
                <div className="bg-yellow-50 rounded-xl shadow p-5 text-center border-2 border-yellow-400">
                  <p className="text-gray-500 text-sm">⏳ Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">{complaintsStats.pending}</p>
                </div>
                <div className="bg-blue-50 rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">🔄 In Progress</p>
                  <p className="text-3xl font-bold text-blue-600">{complaintsStats.in_progress}</p>
                </div>
                <div className="bg-green-50 rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">✅ Resolved</p>
                  <p className="text-3xl font-bold text-green-600">{complaintsStats.resolved}</p>
                </div>
                <div className="bg-red-50 rounded-xl shadow p-5 text-center border-2 border-red-400">
                  <p className="text-gray-500 text-sm">🚨 High Priority</p>
                  <p className="text-3xl font-bold text-red-600">{complaintsStats.high_priority_pending}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="p-4 bg-gray-50 border-b">
                  <h3 className="font-semibold">🎫 {language === 'hi' ? 'Recent Complaints' : 'Recent Complaints'}</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'स्कूल' : 'School'}</th>
                      <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'विषय' : 'Subject'}</th>
                      <th className="px-4 py-2 text-center text-sm">Priority</th>
                      <th className="px-4 py-2 text-center text-sm">Status</th>
                      <th className="px-4 py-2 text-center text-sm">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allComplaints.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{c.school_name}</td>
                        <td className="px-4 py-2 text-sm">{c.subject || c.category}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            c.priority === 'high' ? 'bg-red-100 text-red-700' :
                            c.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {c.priority}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            c.status === 'resolved' ? 'bg-green-100 text-green-700' :
                            c.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {c.status !== 'resolved' && (
                            <button 
                              onClick={() => handleUpdateComplaintStatus(c.id, 'resolved')}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              ✓ Resolve
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">Loading Support...</div>
          )}
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {auditStats ? (
            <>
              {/* Audit Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल Logs (7 days)' : 'Total Logs (7 days)'}</p>
                  <p className="text-3xl font-bold text-blue-600">{auditStats.total_logs}</p>
                </div>
                <div className="bg-red-50 rounded-xl shadow p-5 text-center border-2 border-red-300">
                  <p className="text-gray-500 text-sm">🚨 Critical</p>
                  <p className="text-3xl font-bold text-red-600">{auditStats.critical_count}</p>
                </div>
                <div className="bg-yellow-50 rounded-xl shadow p-5 text-center border-2 border-yellow-300">
                  <p className="text-gray-500 text-sm">⚠️ Warnings</p>
                  <p className="text-3xl font-bold text-yellow-600">{auditStats.warning_count}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">Categories</p>
                  <p className="text-3xl font-bold text-purple-600">{Object.keys(auditStats.by_category || {}).length}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-5 text-center">
                  <p className="text-gray-500 text-sm">Active Users</p>
                  <p className="text-3xl font-bold text-green-600">{auditStats.top_users?.length || 0}</p>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity Feed */}
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                    <h3 className="font-semibold">⚡ {language === 'hi' ? 'Live Activity Feed' : 'Live Activity Feed'}</h3>
                  </div>
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {activityFeed.map((activity, idx) => (
                      <div key={idx} className={`px-4 py-3 flex items-center gap-3 ${
                        activity.severity === 'critical' ? 'bg-red-50' :
                        activity.severity === 'warning' ? 'bg-yellow-50' : ''
                      }`}>
                        <span className="text-2xl">{activity.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{activity.title}</p>
                          <p className="text-xs text-gray-500">{activity.subtitle}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(activity.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Users */}
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="p-4 bg-gray-50 border-b">
                    <h3 className="font-semibold">👤 {language === 'hi' ? 'Most Active Users' : 'Most Active Users'}</h3>
                  </div>
                  <div className="divide-y">
                    {auditStats.top_users?.map((user, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                            {idx + 1}
                          </span>
                          <span className="font-medium">{user.user || 'Unknown'}</span>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {user.count} actions
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Detailed Logs Table */}
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
                  <h3 className="font-semibold">📝 {language === 'hi' ? 'Detailed Audit Logs' : 'Detailed Audit Logs'}</h3>
                  <button 
                    onClick={() => handleExport('transactions')}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200"
                  >
                    📥 Export
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'समय' : 'Time'}</th>
                        <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'Action' : 'Action'}</th>
                        <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'Category' : 'Category'}</th>
                        <th className="px-4 py-2 text-left text-sm">{language === 'hi' ? 'User' : 'User'}</th>
                        <th className="px-4 py-2 text-center text-sm">Severity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {auditLogs.map((log, idx) => (
                        <tr key={idx} className={`hover:bg-gray-50 ${
                          log.severity === 'critical' ? 'bg-red-50' :
                          log.severity === 'warning' ? 'bg-yellow-50' : ''
                        }`}>
                          <td className="px-4 py-2 text-sm">
                            {new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-2 font-medium text-sm">{log.action?.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs">{log.category}</span>
                          </td>
                          <td className="px-4 py-2 text-sm">{log.user_name || 'System'}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              log.severity === 'critical' ? 'bg-red-100 text-red-700' :
                              log.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {log.severity}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">Loading Audit Logs...</div>
          )}
        </div>
      )}

      {/* NBFC Approval Tab */}
      {activeTab === 'nbfc-approval' && (
        <NBFCApprovalManager token={token} />
      )}

      {/* Onboarding Calls Tab */}
      {activeTab === 'onboarding-cal' && (
        <OnboardingAdminPanel token={token} />
      )}

      {/* Diksha Demo Editor Tab */}
      {activeTab === 'diksha-editor' && (
        <DikshaDemoEditor token={token} />
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <BillingAdminPage token={token} />
      )}

      {/* Comments Inbox Tab */}
      {activeTab === 'comments-inbox' && (
        <CommentsInboxPanel token={token} />
      )}

      {/* School Detail Modal */}
      {selectedSchool && schoolDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white flex justify-between items-center z-10">
              <h3 className="text-xl font-bold">🏫 {schoolDetail.school?.name}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShow360(v => !v)}
                  data-testid="school-360-toggle"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${show360 ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}>
                  {show360 ? '↩ Summary' : '📋 Complete Profile (360°)'}
                </button>
                <button onClick={() => {setSelectedSchool(null); setSchoolDetail(null); setShow360(false);}} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
              </div>
            </div>
            {show360 ? (
              <div className="p-6"><SchoolProfile360 schoolId={selectedSchool} token={token} /></div>
            ) : (
            <div className="p-6 space-y-6">
              {/* School Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Users</p>
                  <p className="text-2xl font-bold text-blue-600">{schoolDetail.counts?.users}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Students</p>
                  <p className="text-2xl font-bold text-green-600">{schoolDetail.counts?.students}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Teachers</p>
                  <p className="text-2xl font-bold text-purple-600">{schoolDetail.counts?.teachers}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Wallet</p>
                  <p className="text-2xl font-bold text-orange-600">₹{schoolDetail.wallet?.balance}</p>
                </div>
              </div>

              {/* Plan & AI Usage */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">📋 Plan Details</h4>
                  <p>Plan: <span className="font-bold">{schoolDetail.plan?.plan_name || 'None'}</span></p>
                  <p>Monthly Fee: ₹{schoolDetail.plan?.monthly_fee || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">🤖 AI Usage</h4>
                  <p>This Month: {schoolDetail.ai_usage?.this_month?.calls} calls (₹{schoolDetail.ai_usage?.this_month?.cost})</p>
                  <p>Total: {schoolDetail.ai_usage?.total?.calls} calls (₹{schoolDetail.ai_usage?.total?.cost})</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button 
                  onClick={() => {setAddCreditsSchoolId(selectedSchool); setShowAddCreditsModal(true);}}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  + Add Credits
                </button>
              </div>

              {/* School Permissions Section */}
              <SchoolPermissionsPanel schoolId={selectedSchool} token={token} />

              {/* Recent Transactions */}
              {schoolDetail.recent_transactions?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">💰 Recent Transactions</h4>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    {schoolDetail.recent_transactions.map(tx => (
                      <div key={tx.id} className="flex justify-between text-sm">
                        <span>{tx.category} - {new Date(tx.created_at).toLocaleDateString()}</span>
                        <span className={tx.type === 'credit' ? 'text-green-600' : 'text-red-500'}>
                          {tx.type === 'credit' ? '+' : '-'}₹{tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Add Credits Modal */}
      {showAddCreditsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">➕ Add Credits to School</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'राशि (₹)' : 'Amount (₹)'}</label>
                <input
                  type="number"
                  value={addCreditsAmount}
                  onChange={(e) => setAddCreditsAmount(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'कारण' : 'Reason'}</label>
                <input
                  type="text"
                  value={addCreditsReason}
                  onChange={(e) => setAddCreditsReason(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Promotional credits / Support"
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowAddCreditsModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                <button 
                  onClick={handleAddCredits}
                  disabled={!addCreditsAmount || !addCreditsReason}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg disabled:opacity-50"
                >
                  Add Credits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">📢 {language === 'hi' ? 'Platform Notification भेजें' : 'Send Platform Notification'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'शीर्षक' : 'Title'}</label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Important Update"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'संदेश' : 'Message'}</label>
                <textarea
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Your message here..."
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowNotificationModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                <button 
                  onClick={handleSendNotification}
                  disabled={!notificationTitle || !notificationMessage}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                >
                  📢 Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Data Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">📥 {language === 'hi' ? 'डेटा Export करें' : 'Export Data'}</h3>
            <div className="space-y-3">
              {[
                { type: 'students', icon: '👨‍🎓', label: 'Students', label_hi: 'छात्र' },
                { type: 'teachers', icon: '👨‍🏫', label: 'Teachers', label_hi: 'शिक्षक' },
                { type: 'users', icon: '👤', label: 'Users', label_hi: 'Users' },
                { type: 'fees', icon: '💰', label: 'Fees', label_hi: 'फीस' },
                { type: 'attendance', icon: '📋', label: 'Attendance', label_hi: 'उपस्थिति' },
                { type: 'transactions', icon: '💳', label: 'Transactions', label_hi: 'लेनदेन' },
              ].map(item => (
                <button
                  key={item.type}
                  onClick={() => { handleExport(item.type); }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="font-medium">{language === 'hi' ? item.label_hi : item.label}</span>
                  <span className="ml-auto text-sm text-gray-500">CSV</span>
                </button>
              ))}
            </div>
            <button 
              onClick={() => setShowExportModal(false)} 
              className="w-full mt-4 px-4 py-2 border rounded-lg"
            >
              {language === 'hi' ? 'बंद करें' : 'Close'}
            </button>
          </div>
        </div>
      )}

      <AddSchoolModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} token={token} onSuccess={loadAllData} />
    </div>
  );
}

// Add School Modal

// School Health Score Section
function SchoolHealthSection({ token, language }) {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (token) loadHealth(); }, [token]);

  const loadHealth = async () => {
    try {
      const res = await fetch(`${API_URL}/api/school-health/all/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setHealthData(data.schools || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const gradeColors = {
    'A+': 'bg-green-500', 'A': 'bg-blue-500', 'B': 'bg-yellow-500', 'C': 'bg-orange-500', 'D': 'bg-red-500'
  };

  if (loading) return (
    <div className="bg-white rounded-xl shadow p-5" data-testid="school-health-section">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <i className="fa-solid fa-heart-pulse text-red-500"></i>
        School Health Score
      </h3>
      <div className="flex justify-center py-4"><div className="animate-spin h-6 w-6 border-b-2 border-orange-500 rounded-full"></div></div>
    </div>
  );

  if (!healthData || healthData.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow p-5" data-testid="school-health-section">
      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <i className="fa-solid fa-heart-pulse text-red-500"></i>
        {language === 'hi' ? 'School Health Score' : 'School Health Score'}
      </h3>
      {healthData.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-4">{language === 'hi' ? 'कोई school data नहीं' : 'No school data'}</p>
      ) : (
        <div className="space-y-3">
          {healthData.map(s => (
            <div key={s.school_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition" data-testid={`health-card-${s.school_id}`}>
              <div className={`w-12 h-12 ${gradeColors[s.grade] || 'bg-gray-400'} rounded-xl flex items-center justify-center text-white font-black text-lg`}>
                {s.grade}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">{s.school_name}</p>
                <p className="text-xs text-gray-400">{s.students} students — {s.label}</p>
              </div>
              <div className="w-24">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${gradeColors[s.grade] || 'bg-gray-400'}`} style={{width: `${s.percentage}%`}} />
                  </div>
                  <span className="text-sm font-bold text-gray-700">{s.score}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// School Permissions Panel Component
function SchoolPermissionsPanel({ schoolId, token }) {
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    if (schoolId) loadPerms();
  }, [schoolId]);

  const loadPerms = async () => {
    const data = await api.get(`/api/school-permissions/${schoolId}`, token);
    if (data.success) setPerms(data.permissions);
  };

  const togglePerm = async (key) => {
    setSaving(true);
    const newVal = !perms[key];
    const res = await api.put(`/api/school-permissions/update`, { school_id: schoolId, [key]: newVal }, token);
    if (res.success) setPerms(prev => ({ ...prev, [key]: newVal }));
    setSaving(false);
  };

  if (!perms) return null;

  const permList = [
    { key: 'can_onboard_students', label: language === 'hi' ? 'Student Onboarding' : 'Student Onboarding', icon: 'fa-user-graduate', desc: language === 'hi' ? 'School Admin छात्र जोड़ सकता है' : 'School Admin can add students' },
    { key: 'can_manage_fees', label: language === 'hi' ? 'Fee Management' : 'Fee Management', icon: 'fa-indian-rupee-sign', desc: language === 'hi' ? 'Fee structure और collection' : 'Fee structure & collection' },
    { key: 'can_manage_transport', label: language === 'hi' ? 'Transport Management' : 'Transport Management', icon: 'fa-bus', desc: language === 'hi' ? 'Transport route और GPS tracking' : 'Transport routes & GPS' },
    { key: 'can_manage_hr', label: language === 'hi' ? 'HR & Payroll' : 'HR & Payroll', icon: 'fa-users-gear', desc: language === 'hi' ? 'Staff salary और attendance' : 'Staff salary & attendance' },
    { key: 'can_send_whatsapp', label: language === 'hi' ? 'WhatsApp Messaging' : 'WhatsApp Messaging', icon: 'fa-brands fa-whatsapp', desc: language === 'hi' ? 'WhatsApp broadcast भेजना' : 'Send WhatsApp broadcasts' },
    { key: 'can_use_ai', label: language === 'hi' ? 'AI Features' : 'AI Features', icon: 'fa-robot', desc: language === 'hi' ? '31 AI features का access' : 'Access to 31 AI features' },
  ];

  return (
    <div data-testid="school-permissions-panel">
      <h4 className="font-semibold mb-3 flex items-center gap-2">
        <i className="fa-solid fa-shield-halved text-blue-600"></i>
        {language === 'hi' ? 'School Permissions / अनुमतियाँ' : 'School Permissions'}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {permList.map(p => (
          <div key={p.key} className={`flex items-center justify-between p-3 rounded-xl border transition ${perms[p.key] ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid={`perm-${p.key}`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${perms[p.key] ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
                <i className={`fa-solid ${p.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">{p.label}</p>
                <p className="text-xs text-gray-400">{p.desc}</p>
              </div>
            </div>
            <button
              onClick={() => togglePerm(p.key)}
              disabled={saving}
              className={`relative w-12 h-6 rounded-full transition-colors ${perms[p.key] ? 'bg-green-500' : 'bg-red-400'}`}
              data-testid={`perm-toggle-${p.key}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${perms[p.key] ? 'left-6' : 'left-0.5'}`}></span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PlatformDashboard;
